import { useEffect, useState, useCallback, useMemo } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { RajasthanDiscomData } from "../../../models/rajasthan";
import {
  fetchRvpnData,
  getRajasthanDiscomData,
  getRajasthanForecastDiscomData,
  // ✅ DEMO: imports from DemoRestApi instead of RestApi
} from "../../../Rest_api/DemoRestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { useDispatch, useSelector } from "react-redux";
import { R_CHART_TYPES } from "../const";
import { GnaRuvnlFooter } from "../components";
import { DEFAULT_DATES, DataTabs, DEMAND_UNITS } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import { formatDateForAPI } from "../const";
import { FilterTab } from "../../../components/Filters";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanSummaryAnalysis } from "./TrendSummary";
import { RajasthanPeakAnalysis } from "./TrendPeak_Analysis";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { getTrendMetrics } from "../../Rajasthan/Utility/RestApi";
import { TrendData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_BY = [
  "Discom Load",
  "Total Discom Load",
  "RVPN Drawal",
  "Total Load & RVPN Drawal",
];

// ✅ DEMO: labels and keys use anonymised Discom 1/2/3 naming
const DISCOM = [
  { label: "Discom 1", key: "Discom 1" },
  { label: "Discom 2", key: "Discom 2" },
  { label: "Discom 3", key: "Discom 3" },
];

const X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  { label: "Date", key: "date", values: ["dateIndex"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = DEMAND_UNITS;

// ✅ DEMO: normalise slot_id → block number consistently across all branches
function toBlock(slotId: any, fallback: any): number {
  if (slotId !== undefined && slotId !== null) {
    const n = typeof slotId === "string" ? parseInt(slotId, 10) : slotId;
    if (!isNaN(n)) return n;
  }
  const n = typeof fallback === "string" ? parseInt(fallback, 10) : fallback;
  return isNaN(n) ? fallback : n;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function RajasthanTrend() {
  const dispatch = useDispatch();
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const uniqueDate = useMemo(
    () => DefaultDates[activeTab]?.trendDateOptions || [],
    [DefaultDates, activeTab],
  );

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[2]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedForecastType, setSelectedForecastType] = useState<string[]>([
    "Actual",
    "Forecast D+1",
  ]);

  // ── Chart suffixes ─────────────────────────────────────────────────────────
  const chartSuffixes = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      return [
        {
          suffix: "drawal",
          displayName: "RVPN Drawal",
          stackSuffix: "rvpnl_drawal",
        },
        {
          suffix: "deviation",
          displayName: "Deviation %",
          stackSuffix: "deviation",
        },
      ];
    }
    if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      return [
        { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
        {
          suffix: "deviation",
          displayName: "Deviation %",
          stackSuffix: "deviation",
        },
      ];
    }
    return [
      { suffix: "schedule", displayName: "Schedule", stackSuffix: "schedule" },
      { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
      {
        suffix: "load_shedding",
        displayName: "Load Shedding",
        stackSuffix: "load_shedding",
      },
      { suffix: "total", displayName: "Total Load", stackSuffix: "total" },
    ];
  }, [selectedGroupKeys]);

  const [selectedSuffix, setSelectedSuffix] = useState(
    chartSuffixes.slice(0, 2),
  );

  useEffect(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      setSelectedSuffix(chartSuffixes);
      setSelectedForecastType(["Actual", "Forecast D+1"]);
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      setSelectedSuffix(chartSuffixes.filter((s) => s.suffix === "drawal"));
      setSelectedForecastType(["Actual"]);
    } else {
      setSelectedSuffix(chartSuffixes.slice(0, 2));
    }
  }, [chartSuffixes, selectedGroupKeys]);

  // ── Chart object factory ───────────────────────────────────────────────────
  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      xAxis: { isTime: boolean },
      chartType: { value: string; stackId?: string },
      colors: string[],
    ) => {
      const displayKey = normalizeDisplayLabelWithoutUndefined(key)
        .replace(/T\+1/g, "D+1")
        .replace(/T\+2/g, "D+2")
        .replace(/rvpn/g, "RVPN")
        .replace(/total_discom/g, "Total Discom");

      const stroke =
        suffix === "schedule"
          ? lightenColor(colors[index], 25)
          : suffix === "drawal"
            ? colors[index]
            : suffix === "deviation"
              ? "#ff6b6b"
              : suffix === "total" &&
                  selectedGroupKeys === "Total Load & RVPN Drawal"
                ? lightenColor(colors[index], 35)
                : suffix === "total"
                  ? lightenColor(colors[index], 15)
                  : suffix === "load_shedding"
                    ? lightenColor(colors[index], 45)
                    : undefined;

      return {
        dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
        name: `${displayKey} ${displayName}`,
        yAxisId: suffix === "deviation" ? "right" : undefined,
        stroke,
        type: chartType.value,
        stackId: chartType.stackId !== undefined ? stackSuffix : undefined,
      };
    },
    [selectedUnit, selectedGroupKeys],
  );

  // ── Data fetching ──────────────────────────────────────────────────────────
  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;

      const processedDates = [
        ...originalDates,
        ...originalDates
          .filter((d: any) => d.label.replace(" to ", ",") === d.value)
          .map((d: any) => ({
            forecast: true,
            isFixed: false,
            label: d.label + " (Forecast)",
            value: d.value + ",Model 1",
            model: "Model 1",
          })),
      ];

      const promises = processedDates.map(async (date: any) => {
        try {
          const [startDate, endDate, model] = date.value.split(",");

          // ── Forecast branch ──────────────────────────────────────────────
          if (model) {
            if (
              selectedGroupKeys !== "RVPN Drawal" &&
              selectedGroupKeys !== "Total Load & RVPN Drawal"
            )
              return [];

            const forecastedData = await getRajasthanForecastDiscomData(
              startDate,
              endDate,
              model,
            );

            if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              return forecastedData.map((item: any) => ({
                ...item,
                dates: date.label.replace(")", " ") + item.forcast_type + ")",
                discom: "rvpn",
                block: toBlock(item.slot_id, item.block), // ✅ DEMO
              }));
            }
            return forecastedData.map((item: any) => ({
              ...item,
              dates: date.label.replace(")", " ") + item.forcast_type + ")",
              block: toBlock(item.slot_id, item.block), // ✅ DEMO
            }));
          }

          // ── Actual branch ────────────────────────────────────────────────
          if (selectedGroupKeys === "RVPN Drawal") {
            return (await fetchRvpnData(startDate, endDate)).map(
              (item: any) => ({
                ...item,
                dates: date.label,
                discom: "total",
                // ✅ fetchRvpnData already sets block = slot internally; slot_id kept for compat
                block: toBlock(item.block, item.slot),
              }),
            );
          }

          if (selectedGroupKeys === "Total Load & RVPN Drawal") {
            const [discomData, rvpnData] = await Promise.all([
              getRajasthanDiscomData(startDate, endDate),
              fetchRvpnData(startDate, endDate),
            ]);

            // ✅ DEMO: preserve discom key as-is (already "Discom 1/2/3" from DemoRestApi)
            const discomMapped = discomData.map((item: any) => ({
              ...item,
              dates: date.label,
              discom: "total_discom",
              block: toBlock(item.slot_id, item.block),
            }));

            const rvpnMapped = rvpnData.map((item: any) => ({
              ...item,
              dates: date.label,
              discom: "rvpn",
              block: toBlock(item.block, item.slot), // ✅ fetchRvpnData sets block=slot
              schedule_mw: 0,
              schedule_mwh: 0,
              schedule_lu: 0,
              load_shedding_mw: 0,
              load_shedding_mwh: 0,
              load_shedding_lu: 0,
            }));

            return [...discomMapped, ...rvpnMapped];
          }

          // Default: Discom Load / Total Discom Load
          // ✅ DEMO: preserve discom key as-is (already "Discom 1/2/3" from DemoRestApi)
          return (await getRajasthanDiscomData(startDate, endDate)).map(
            (item: any) => ({
              ...item,
              dates: date.label,
              discom: item.discom,
              block: toBlock(item.slot_id, item.block), // ✅ DEMO
            }),
          );
        } catch (err) {
          console.error(`Error processing date ${date.label}:`, err);
          return [];
        }
      });

      const newData = (await Promise.all(promises)).flat();

      // ── total_mw re-sync & chronological sort for month view ──────────────
      let transformed = newData.map((item) => ({
        ...item,
        total_mw: (item.load_shedding_mw || 0) + (item.drawal_mw || 0),
      }));

      if (selectedXaxis.key === "month") {
        transformed = transformed.sort((a, b) => {
          const toSortKey = (m: string) => {
            const monthMap: Record<string, number> = {
              Jan: 0,
              Feb: 1,
              Mar: 2,
              Apr: 3,
              May: 4,
              Jun: 5,
              Jul: 6,
              Aug: 7,
              Sep: 8,
              Oct: 9,
              Nov: 10,
              Dec: 11,
            };
            const [mon, yr] = (m || "").split("-");
            const y = parseInt(yr ?? "0");
            return isNaN(y) ? 999999 : (2000 + y) * 100 + (monthMap[mon] ?? 99);
          };
          return toSortKey(a.month) - toSortKey(b.month);
        });
      }

      setData(transformed);
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (err) {
      console.error("Error in data fetching:", err);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedGroupKeys, selectedXaxis.key]);

  // ── Chart data processing ──────────────────────────────────────────────────
  useEffect(() => {
    let filteredData = data;

    if (
      selectedGroupKeys !== "RVPN Drawal" &&
      selectedGroupKeys !== "Total Load & RVPN Drawal" &&
      selectedDiscom.length > 0
    ) {
      // ✅ DEMO: item.discom is already "Discom 1/2/3" — filter matches DISCOM keys above
      filteredData = data.filter((item) =>
        selectedDiscom.includes(item.discom),
      );
    }

    if (selectedBlock.length > 0) {
      const blockValues = selectedBlock.map((opt) => opt.value);
      filteredData = filteredData.filter((item) =>
        blockValues.includes(item.block),
      );
    }

    const unit = selectedUnit.toLowerCase();
    const aggregationKeys = [
      "schedule",
      "drawal",
      "load_shedding",
      "total",
    ].map((k) => `${k}_${unit}`);

    const par: ChartDataParams = {
      keys: handleKey() ?? [],
      tableKeys: ["dateIndex", "block", "month", "date"],
      groupBy:
        selectedGroupKeys === "RVPN Drawal"
          ? ["dates"]
          : selectedGroupKeys === "Total Load & RVPN Drawal"
            ? ["dates", "discom"]
            : ["dates"].concat(
                selectedGroupKeys === "Discom Load" ? ["discom"] : [],
              ),
      volumeKey: "",
      aggregationKeys,
      averageKeys: ["schedule_mw", "drawal_mw", "load_shedding_mw", "total_mw"],
      averageDuplicationKeys: ["block", "dateIndex"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    const needsDeviation =
      selectedGroupKeys === "RVPN Drawal" ||
      selectedGroupKeys === "Total Load & RVPN Drawal";

    if (needsDeviation) {
      const processedData = cData.data.map((dataPoint) => {
        const updated = { ...dataPoint };
        cData.keys.forEach((key: string) => {
          const isRelevantForecast =
            selectedGroupKeys === "RVPN Drawal"
              ? key.includes("Forecast")
              : key.includes("Forecast") && key.includes("rvpn");

          if (!isRelevantForecast) return;

          const baseDateKey = key
            .replace(" (Forecast T+1)", "")
            .replace(" (Forecast T+2)", "");
          const actual = dataPoint[`${baseDateKey}_drawal_${unit}`] ?? 0;
          const forecast = dataPoint[`${key}_drawal_${unit}`] ?? 0;
          const deviation =
            actual !== 0 && forecast !== 0
              ? (Math.abs(actual - forecast) / actual) * 100
              : 0;

          updated[`${key}_deviation_${unit}`] = deviation;
        });
        return updated;
      });
      setChartData(processedData);
    } else {
      setChartData(cData.data);
    }

    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedUnit,
    selectedDiscom,
    selectedBlock,
    selectedSuffix,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;

      const ranges = originalDates
        .filter((date: any) => {
          const commaParts = date.value.split(",");
          return commaParts.length <= 2;
        })
        .map((date: any) => {
          let start: string;
          let end: string;

          if (date.value.includes(" to ")) {
            [start, end] = date.value
              .split(" to ")
              .map((s: string) => s.trim());
          } else {
            const parts = date.value.split(",").map((s: string) => s.trim());
            start = parts[0];
            end = parts[1] ?? parts[0];
          }

          return {
            startDate: formatDateForAPI(new Date(start)),
            endDate: formatDateForAPI(new Date(end)),
            blocks: selectedBlock.map((block: any) => block.value),
          };
        })
        .filter((range: any) => range.startDate && range.endDate);

      if (ranges.length === 0) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }
      const discom = selectedDiscom.length === 0 ? "Total" : selectedDiscom[0];
      const metricsData = await getTrendMetrics({
        ranges,
        aggregated: true,
        discom,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock, selectedDiscom]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedXaxis.key === "time") return ["date", "dateIndex", "block"];
      if (selectedXaxis.key === "date") return ["date", "dateIndex"];
      if (selectedXaxis.key === "month") return ["month"];
    }
    if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
      return selectedXaxis.values;
    }
    return selectedXaxis.values;
  }, [selectedBlock, selectedXaxis]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
    if (GROUP_BY[index] === "RVPN Drawal") {
      setSelectedForecastType(["Actual", "Forecast D+1"]);
    } else if (GROUP_BY[index] === "Total Load & RVPN Drawal") {
      setSelectedForecastType(["Actual"]);
    }
  }

  function handleXAxisModeChange(_item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(unit: string) {
    setSelectedUnit(unit);
  }
  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function getTypes(): string[] {
    if (selectedGroupKeys === "RVPN Drawal")
      return ["Actual", "Forecast D+1", "Forecast D+2", "Deviation %"];
    if (selectedGroupKeys === "Total Load & RVPN Drawal")
      return ["Drawal", "Actual"];
    return ["Drawal", "Schedule", "Load Shedding", "Total Load"];
  }

  function handleTypeChange(selected: string[]) {
    if (selectedGroupKeys === "RVPN Drawal") {
      const forecastTypes = selected.filter((t) =>
        ["Actual", "Forecast D+1", "Forecast D+2"].includes(t),
      );
      const drawalSuffix = chartSuffixes.find((s) => s.suffix === "drawal");
      const newSuffixes = drawalSuffix ? [drawalSuffix] : [];
      if (selected.includes("Deviation %")) {
        const devSuffix = chartSuffixes.find((s) => s.suffix === "deviation");
        if (devSuffix) newSuffixes.push(devSuffix);
      }
      setSelectedSuffix(newSuffixes);
      setSelectedForecastType(forecastTypes);
      dispatch(setLoadingFilterName(""));
      return;
    }

    if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const newSuffixes = selected.includes("Drawal")
        ? chartSuffixes.filter((s) => s.suffix === "drawal")
        : [];
      setSelectedSuffix(newSuffixes);
      setSelectedForecastType(selected.includes("Actual") ? ["Actual"] : []);
      dispatch(setLoadingFilterName(""));
      return;
    }

    setSelectedSuffix(
      chartSuffixes.filter((s) => selected.includes(s.displayName)),
    );
  }

  function getSelectedTypes(): string[] {
    if (selectedGroupKeys === "RVPN Drawal") {
      return [
        ...selectedForecastType,
        ...(selectedSuffix.find((s) => s.suffix === "deviation")
          ? ["Deviation %"]
          : []),
      ];
    }
    if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      return [
        ...(selectedSuffix.find((s) => s.suffix === "drawal")
          ? ["Drawal"]
          : []),
        ...(selectedForecastType.includes("Actual") ? ["Actual"] : []),
      ];
    }
    return selectedSuffix.map((s) => s.displayName);
  }

  // ── Legend computation ─────────────────────────────────────────────────────
  const legends = useMemo(() => {
    return selectedSuffix
      .flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys
          .filter((key: string) => {
            if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              return key.includes("total_discom") || key.includes("rvpn");
            }
            return true;
          })
          .map((key: string, index: number) => {
            if (selectedGroupKeys === "Total Load & RVPN Drawal") {
              if (key.includes("Forecast")) return null;
              if (
                suffix === "deviation" &&
                (!key.includes("rvpn") || !key.includes("Forecast"))
              )
                return null;
              if (
                key.includes("rvpn") &&
                ["schedule", "load_shedding", "total"].includes(suffix)
              )
                return null;
              if (key.includes("total_discom") && suffix === "deviation")
                return null;
            } else if (selectedGroupKeys === "RVPN Drawal") {
              if (suffix === "deviation" && !key.includes("Forecast"))
                return null;
            } else if (suffix === "deviation") {
              return null;
            }
            return createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            );
          }),
      )
      .filter(Boolean)
      .filter(
        (item: any) =>
          !item.name?.includes("Forecast") || !item.name?.includes("Schedule"),
      )
      .filter(
        (item: any) =>
          !item.name?.includes("Forecast") ||
          !item.name?.includes("Load Shedding"),
      )
      .filter((item: any) => {
        const name: string = item.name ?? "";

        if (selectedGroupKeys === "Total Load & RVPN Drawal") {
          if (name.includes("rvpn")) {
            if (name.includes("Forecast") || name.includes("Deviation")) {
              return selectedForecastType.some((t) => {
                const n = t.replace("Forecast ", "");
                return name.includes(n) || name.includes(n.replace("D+", "T+"));
              });
            }
            return selectedForecastType.includes("Actual");
          }
          if (name.includes("total_discom")) return true;
        }

        if (selectedGroupKeys === "RVPN Drawal") {
          if (name.includes("Deviation %")) {
            return selectedForecastType.some((t) => {
              const n = t.replace("Forecast ", "");
              return name.includes(n) || name.includes(n.replace("D+", "T+"));
            });
          }
          if (name.includes("Forecast")) {
            return selectedForecastType.some((t) => {
              const n = t.replace("Forecast ", "");
              return name.includes(n) || name.includes(n.replace("D+", "T+"));
            });
          }
          if (!name.includes("Forecast") && !name.includes("Deviation"))
            return selectedForecastType.includes("Actual");
        }

        if (!name.includes("Forecast") && !name.includes("Deviation %"))
          return selectedForecastType.includes("Actual");

        if (name.includes("Deviation %")) {
          if (selectedForecastType.length === 0) return false;
          return selectedForecastType.some((t) => {
            const n = t.replace("Forecast ", "");
            return name.includes(n) || name.includes(n.replace("D+", "T+"));
          });
        }

        return true;
      });
  }, [
    selectedSuffix,
    chartKeys,
    selectedGroupKeys,
    selectedForecastType,
    selectedXaxis,
    selectedChartType,
    createChartObject,
  ]);

  // ── Misc helpers ───────────────────────────────────────────────────────────
  function summaryExtraKeys() {
    if (selectedXaxis.isTime) return ["block", "date", "dateIndex"];
    if (selectedXaxis.key === "date") return ["date", "dateIndex"];
    return [selectedXaxis.values[0]];
  }

  const summaryExtraLabels = selectedXaxis.isTime
    ? ["block", "Date", "dateIndex"]
    : selectedXaxis.key === "date"
      ? ["Date", "dateIndex"]
      : [selectedXaxis.values[0]];

  const xDataKey = useMemo(
    () => (selectedXaxis.key === "time" ? "block" : selectedXaxis.values[0]),
    [selectedXaxis],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends as any);

  const commonSummaryProps = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load Trend Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  // ── Sub-page routing ───────────────────────────────────────────────────────
  const sharedAnalysisProps = {
    onXAxisModeChange: handleXAxisModeChange,
    selectedUnit,
    onUnitChange: handleUnitChange,
    selectedDiscom,
    onDiscomChange: handleDiscomChange,
    selectedGroupKeys,
    onGroupByChange: _handleGroupByChange,
    selectedSuffix,
    onTypeChange: handleTypeChange,
    chartSuffixes,
    metrics,
  };

  if (selectedXAxisMode.value === "peak")
    return <RajasthanPeakAnalysis {...sharedAnalysisProps} />;
  if (selectedXAxisMode.value === "summary")
    return <RajasthanSummaryAnalysis {...sharedAnalysisProps} />;

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((m) => m.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((x) => x.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(_item, index) => setSelectedXaxis(X_AXIS[index])}
          />
          <FilterMenu
            title="Discom Load"
            items={DISCOM.map((d) => d.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={(item) => {
              if (selectedGroupKeys === "RVPN Drawal") {
                dispatch(setLoadingFilterName(""));
                return;
              }
              setSelectedDiscom(item);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(_item, index) => _handleGroupByChange(index)}
          />
          {selectedGroupKeys !== "Total Load & RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={getTypes()}
              selectedItems={getSelectedTypes()}
              setSelectedItems={handleTypeChange}
            />
          )}
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((c) => c.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Analysis"
              subtitle="Fetching Demo State demand trend analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  {selectedGroupKeys} ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 w-full justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((b) => b.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    className="downloadButton"
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(commonSummaryProps);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(commonSummaryProps);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  brushIndex={
                    chartData.length > 96
                      ? { startIndex: 0, endIndex: 95 }
                      : undefined
                  }
                  isTimeSlot={selectedXaxis.isTime}
                  onlyTitle={true}
                  secondYAxisDomain={[0, 100]}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  secondYAxisLabel={
                    selectedSuffix.some((s) => s.suffix === "deviation")
                      ? "Deviation %"
                      : undefined
                  }
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  xDataKey={xDataKey}
                  secondXDataKey={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? selectedXaxis.values[1]
                      : undefined
                  }
                  xTick={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => renderHourTick(value, 10)
                      : undefined
                  }
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>

        {/* ✅ DEMO: simplified footer — no Source tooltip, shows "Demo Version" */}
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
